import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { OnboardingService } from '../onboarding/onboarding.service';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { Card, OnboardingForm } from 'src/app/shared/Models/onboarding-model';
import {
  WorkerCertificate,
  CertificateStatus,
  CertificateType,
  CertificationMonitorSummary,
  CertificationAlert
} from './safety-certification.models';

/**
 * Service for managing worker certification monitoring
 * Bridges Safety module with Onboarding module data
 */
@Injectable({
  providedIn: 'root'
})
export class SafetyCertificationService {
  constructor(private onboardingService: OnboardingService, private logger: LoggerService) {}

  /**
   * Load all certificates for workers in a project
   */
  getProjectCertifications(projectId: number): Observable<WorkerCertificate[]> {
    return this.onboardingService
      .getOnboardingForms(projectId)
      .pipe(
        map((forms: OnboardingForm[]) => {
          const certificates: WorkerCertificate[] = [];

          forms.forEach((form: OnboardingForm) => {
            // Process safety cards
            if (form.formCards && form.formCards.length > 0) {
              form.formCards.forEach((card: Card) => {
                if (!this.isValidExpiryDate(card.expiryDate)) {
                  return;
                }

                const expiryDate = this.parseExpiryDate(card.expiryDate);
                const daysUntilExpiry = this.calculateDaysUntilExpiry(expiryDate);

                certificates.push({
                  id: card.id ? String(card.id) : `${form.formId}-${card.name}`,
                  formId: String(form.formId),
                  workerId: form.workerId,
                  workerName: form.workerName,
                  workerEmail: form.workerEmail,
                  projectId: form.projectId?.toString() || '',
                  projectName: form.projectName || '',
                  certificateName: card.name,
                  certificateType: card.name as CertificateType,
                  description: card.description,
                  expiryDateString: card.expiryDate,
                  expiryDate: expiryDate,
                  daysUntilExpiry,
                  status: this.calculateStatus(daysUntilExpiry),
                  isExpired: daysUntilExpiry < 0,
                  signedDate: form.signedDate ? new Date(form.signedDate) : undefined,
                  adminChecked: form.adminChecked || false
                });
              });
            }

            // Note: Equipment certifications (formMachineUsageRights) handled separately
            // as they typically don't have expiry dates in the current model
          });

          return certificates;
        }),
        catchError(error => {
          this.logger.errorWithContext('SafetyCertificationService', 'Error loading project certifications:', error);
          return of([]);
        })
      );
  }

  /**
   * Get certificates expiring within specified days
   */
  getCertificationsExpiringWithin(
    projectId: number,
    daysOut: number
  ): Observable<WorkerCertificate[]> {
    return this.getProjectCertifications(projectId).pipe(
      map(certs =>
        certs.filter(cert => {
          const daysUntil = cert.daysUntilExpiry;
          return daysUntil >= 0 && daysUntil <= daysOut;
        })
      )
    );
  }

  /**
   * Get all expired certificates
   */
  getExpiredCertifications(projectId: number): Observable<WorkerCertificate[]> {
    return this.getProjectCertifications(projectId).pipe(
      map(certs => certs.filter(cert => cert.isExpired))
    );
  }

  /**
   * Get certification summary for a project
   */
  getCertificationSummary(projectId: number): Observable<CertificationMonitorSummary> {
    return this.getProjectCertifications(projectId).pipe(
      map(certs => {
        const summary: CertificationMonitorSummary = {
          total: certs.length,
          expired: 0,
          critical: 0,
          warning: 0,
          valid: 0,
          byType: {} as Record<CertificateType, number>,
          byStatus: {
            expired: 0,
            critical: 0,
            warning: 0,
            valid: 0
          },
          lastUpdated: new Date()
        };

        certs.forEach(cert => {
          // Count by status
          summary.byStatus[cert.status]++;

          switch (cert.status) {
            case 'expired':
              summary.expired++;
              break;
            case 'critical':
              summary.critical++;
              break;
            case 'warning':
              summary.warning++;
              break;
            case 'valid':
              summary.valid++;
              break;
          }

          // Count by type
          if (!summary.byType[cert.certificateType]) {
            summary.byType[cert.certificateType] = 0;
          }
          summary.byType[cert.certificateType]++;
        });

        return summary;
      }),
      catchError(error => {
        this.logger.errorWithContext('SafetyCertificationService', 'Error calculating certification summary:', error);
        return of({
          total: 0,
          expired: 0,
          critical: 0,
          warning: 0,
          valid: 0,
          byType: {} as Record<CertificateType, number>,
          byStatus: {
            expired: 0,
            critical: 0,
            warning: 0,
            valid: 0
          },
          lastUpdated: new Date()
        });
      })
    );
  }

  /**
   * Generate alerts for critical and warning certifications
   */
  generateCertificationAlerts(projectId: number): Observable<CertificationAlert[]> {
    return this.getProjectCertifications(projectId).pipe(
      map(certs => {
        return certs
          .filter(cert => cert.status === 'critical' || cert.status === 'expired')
          .map(cert => ({
            id: `${cert.id}-alert`,
            workerId: cert.workerId,
            workerName: cert.workerName,
            certificateType: cert.certificateType,
            certificateName: cert.certificateName,
            expiryDate: cert.expiryDate,
            daysUntilExpiry: cert.daysUntilExpiry,
            severity: cert.isExpired ? 'high' : cert.daysUntilExpiry <= 7 ? 'high' : 'medium',
            projectId: cert.projectId,
            projectName: cert.projectName
          }));
      })
    );
  }

  /**
   * Parse MM/YYYY expiry date string to Date object
   */
  private parseExpiryDate(dateString: string): Date {
    if (!dateString || typeof dateString !== 'string') {
      return new Date(0);
    }

    const parts = dateString.split('/');
    if (parts.length !== 2) {
      return new Date(0);
    }

    const month = parseInt(parts[0], 10) - 1; // JS months are 0-indexed
    const year = parseInt(parts[1], 10);

    if (isNaN(month) || isNaN(year)) {
      return new Date(0);
    }

    // Set to end of the month
    return new Date(year, month + 1, 0, 23, 59, 59);
  }

  private isValidExpiryDate(dateString: string): boolean {
    if (!dateString || typeof dateString !== 'string') {
      return false;
    }

    const parts = dateString.split('/');
    if (parts.length !== 2) {
      return false;
    }

    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);

    return !isNaN(month) && !isNaN(year) && month >= 1 && month <= 12 && year >= 2000;
  }

  /**
   * Calculate days until expiry (-1 = expired)
   */
  private calculateDaysUntilExpiry(expiryDate: Date): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((expiry.getTime() - today.getTime()) / millisecondsPerDay);
  }

  /**
   * Calculate certification status based on days until expiry
   */
  private calculateStatus(daysUntilExpiry: number): CertificateStatus {
    if (daysUntilExpiry < 0) {
      return 'expired';
    }
    if (daysUntilExpiry <= 7) {
      return 'critical';
    }
    if (daysUntilExpiry <= 30) {
      return 'warning';
    }
    return 'valid';
  }
}
